import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Stack,
  Typography,
  Paper,
  Grid,
  Box,
} from '@mui/material';
import JsFileDownloader from 'js-file-download';
import Loading from '../table-elements/loading.component';
import ROUTE_PATH from '../../config/routes.config';
import {
  getSubscriberAttemptDetails,
  viewDataById,
} from '../../services/quiz.service';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  LISTING_COLUMNS,
  LISTING_ACTIONS,
} from '../../config/module-configs/quiz-result.config';
import ListData from '../table-elements/list-data.component';
import useFilteredActions from '../../hooks/filtered-action.hook';
import { AccessContext } from '../../context/access.context';
import SearchList from '../table-elements/search-list.component';
import useAccess from '../../hooks/access.hook';
import { formatTimestamp, secondsToHMS } from '../../utils/datetime.util';
import { ACTIONS } from '../../config/const.config';
import editUrl from '../../utils/url-edit.util';
import Iconify from '../common/iconify.component';

const ViewQuizResults = ({ id }) => {
  const columns = LISTING_COLUMNS;
  const { accessData } = useContext(AccessContext);
  const { hasOperationAccess } = useAccess();
  const navigate = useNavigate();
  const searchQuizRef = useRef(null);
  const actions = useFilteredActions(accessData.role, 'quiz', LISTING_ACTIONS);
  const [loading, setLoading] = useState(true);
  const [quizDetails, setQuizDetails] = useState(null);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 10,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });

  const getQuizDetails = async (_id) => {
    setLoading(true);
    const response = await viewDataById(_id);
    setQuizDetails(response?.data);
  };

  useEffect(() => {
    const params = [];
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    if (options.sortBy && options.sortOrder) {
      params.push(`sortBy=${options.sortBy}`);
      params.push(`sortOrder=${options.sortOrder}`);
    }
    if (options.searchValue) {
      params.push(`q=${options.searchValue}`);
    }
    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    getQuizDetails(id)
      .then(() => {
        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          type: 'error',
          message,
          show: true,
        });
        setLoading(false);
      });

    getSubscriberAttemptDetails(id, paramsQuery)
      .then((res) => {
        setOptions({
          ...options,
          loading: false,
          rows: res?.data?.rows || [],
          totalRows: res?.data?.totalRows || 0,
          error: false,
        });
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          type: 'error',
          message,
          show: true,
        });
      });
  }, [options.reloadCounter]);

  const downloadReport = () => {
    const params = [];
    params.push(`output=csv`);
    params.push(`type=export`);
    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    const fileName = `${quizDetails.title}_report`;
    getSubscriberAttemptDetails(id, paramsQuery, 'csv')
      .then((res) => {
        // eslint-disable-next-line no-unused-vars
        const download = new JsFileDownloader(res, `${fileName}.csv`);
      })
      .catch(() => {});
  };

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleActionSelect = (selectedAction, selectedData) => {
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };

  const handleSearchGroups = () => {
    if (options.loading) return;
    setOptions({
      ...options,
      q: searchQuizRef.current.value,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
      page: 0,
    });
  };

  const handleSearchChange = (e) => {
    setOptions({
      ...options,
      searchValue: e.target.value || '',
    });
  };

  const handleClearSearch = () => {
    if (options.loading) return;
    setOptions({
      ...options,
      searchValue: '',
      q: null,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
    searchQuizRef.current.value = '';
  };

  return (
    <>
      {loading && <Loading />}

      {!loading && (
        <div>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ py: 2 }}
          >
            <Box>
              <Link to={ROUTE_PATH.QUIZ}>
                <Button
                  color="inherit"
                  variant="contained"
                  startIcon={<Iconify icon="ic:arrow-back" />}
                >
                  Back
                </Button>
              </Link>

              {hasOperationAccess(accessData.role, 'quiz', 'view') && (
                <SearchList
                  label="Subscriber"
                  handleSearch={handleSearchGroups}
                  inputRef={searchQuizRef}
                  onChange={handleSearchChange}
                  options={options}
                  clearSearch={handleClearSearch}
                  loading={options.loading}
                  width="200px"
                />
              )}
            </Box>
            <Button variant="contained" sx={{ my: 2 }} onClick={downloadReport}>
              Download CSV
            </Button>
          </Stack>

          {quizDetails && (
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h5" gutterBottom>
                    {quizDetails.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {quizDetails.description}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Allowed Attempts Per Subscriber
                  </Typography>
                  <Typography variant="body1">
                    {quizDetails.allowedAttempts}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Quiz Start Time
                  </Typography>
                  <Typography variant="body1">
                    {formatTimestamp(quizDetails.startTime, 'lll')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Quiz End Time
                  </Typography>
                  <Typography variant="body1">
                    {formatTimestamp(quizDetails.endTime, 'lll')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Passing Criteria
                  </Typography>
                  <Typography variant="body1">
                    {quizDetails.passCriteria} %
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Unique Subscribers
                  </Typography>
                  <Typography variant="body1">
                    {quizDetails.uniqueSubscribers || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Attempts
                  </Typography>
                  <Typography variant="body1">
                    {quizDetails.totalAttempts}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Average Time Taken
                  </Typography>
                  <Typography variant="body1">
                    {secondsToHMS(Math.floor(quizDetails.averageTime))}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Average Score
                  </Typography>
                  <Typography variant="body1">
                    {quizDetails.averageScore} %
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          <Card>
            <ListData
              columns={columns}
              rows={options.rows}
              page={options.page}
              rowsPerPage={options.rowsPerPage}
              totalRows={options.totalRows}
              loading={options.loading}
              actions={actions}
              error={options.error}
              sortBy={options.sortBy}
              sortOrder={options.sortOrder}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              onSortChange={handleSortingChange}
              onAction={handleActionSelect}
            />
          </Card>
        </div>
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}

      {doAction.action === ACTIONS.VIEW.value &&
        navigate(
          editUrl(ROUTE_PATH.QUIZ_SUBSCRIBER_RESULTS, id, doAction.data.id)
        )}
    </>
  );
};

ViewQuizResults.propTypes = {
  id: PropTypes.number.isRequired,
};

export default ViewQuizResults;
